// framework/client/core/Logger.js

const LOG_LEVELS = {
    DEBUG: 10,
    INFO: 20,
    WARN: 30,
    ERROR: 40,
    NONE: 50,
};

export class Logger {
    constructor(moduleName, appClient) {
        this.moduleName = moduleName;
        this.appClient = appClient; // Store the ApplicationClient instance
        this.localStorageKey = "logLevel";
        this.logLevel = this.getLogLevel(); // Get initial log level
        this.colorStyles = this.getColorStyles(); // Get color styles

        // Create a proxy to intercept method calls
        return new Proxy(this, {
            get(target, propKey, receiver) {
                if (['info', 'warn', 'error', 'debug'].includes(propKey)) {
                    const level = propKey.toUpperCase();
                    if (LOG_LEVELS[level] >= LOG_LEVELS[target.logLevel]) {
                        // Return the actual bound method
                        return Reflect.get(target, `_${propKey}`, receiver).bind(target);
                    } else {
                        return () => { }; // Return a no-op function
                    }
                }
                // For other properties, return them directly
                return Reflect.get(target, propKey, receiver);
            }
        });
    }

    getLogLevel() {
        try {
            const storedLevel = localStorage.getItem(this.localStorageKey);
            if (storedLevel && LOG_LEVELS[storedLevel.toUpperCase()] !== undefined) {
                return storedLevel.toUpperCase();
            }
        } catch (error) {
            console.warn("Error accessing localStorage:", error);
            // If localStorage is not accessible (e.g., disabled, incognito mode),
            // we'll fall back to the default.
        }
        return 'WARN'; // Default log level
    }

    setLogLevel(level) {
        const upperLevel = level.toUpperCase();
        if (LOG_LEVELS[upperLevel] !== undefined) {
            this.logLevel = upperLevel;
            try {
                localStorage.setItem(this.localStorageKey, this.logLevel);
            } catch (error) {
                console.warn("Error saving to localStorage:", error);
                // If localStorage fails, we still update the in-memory logLevel.
            }
            this.log.info(`Log level set to ${this.logLevel}`);
        } else {
            console.warn(`Invalid log level: ${level}`);
        }
    }

    getColorStyles() {
        // Get computed styles for the root element (where Bootstrap variables are defined)
        const rootStyles = getComputedStyle(document.documentElement);

        return {
            info: `color: ${rootStyles.getPropertyValue('--bs-info').trim()};`,
            warn: `color: ${rootStyles.getPropertyValue('--bs-warning').trim()};`,
            error: `color: ${rootStyles.getPropertyValue('--bs-danger').trim()};`,
            debug: `color: ${rootStyles.getPropertyValue('--bs-secondary').trim()};`, // Use secondary for debug
        };
    }


    _info(...args) {
        console.log(`%c[INFO] [${this.moduleName}]`, this.colorStyles.info, ...args);
    }

    _warn(...args) {
        console.warn(`%c[WARN] [${this.moduleName}]`, this.colorStyles.warn, ...args);
    }

    _error(...args) {
        console.error(`%c[ERROR] [${this.moduleName}]`, this.colorStyles.error, ...args);
    }

    _debug(...args) {
        console.debug(`%c[DEBUG] [${this.moduleName}]`, this.colorStyles.debug, ...args);
    }
}